import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  BufferTankOperatingModeType,
  DevicesStore,
  InstallationFeaturesStore,
  isReady,
} from '@store';
import {
  ActiveModeCommands,
  Feature,
  FeatureActionFields,
  FeatureCommands,
  FeatureNames,
  FeaturePropertyKey,
  IsStringValueProperty,
  StatusValueDefaultFeature,
} from '@vi-iot/client/index';
import { isEqual } from 'lodash';
import { distinctUntilChanged, filter, Observable, Subject, take, takeUntil } from 'rxjs';
import { BufferTankOverlayHelper } from './buffer-tank-overlay.component.helper';

@Component({
  selector: 'app-buffer-tank-overlay',
  templateUrl: './buffer-tank-overlay.component.html',
  styleUrl: './buffer-tank-overlay.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BufferTankOverlayComponent implements OnInit, OnDestroy {
  readonly featurePropertyKey = FeaturePropertyKey;
  isOperatingModeEditable = true;

  availableBufferModes: BufferTankOverlayHelper.BufferTankMode[] = [
    {
      value: BufferTankOperatingModeType.Cooling,
      viewValue: this.translate.instant(
        '+INSTALLATIONS.INSTALLATION.PANEL.BUFFER_TANK.MODE_COOLING'
      ),
    },
    {
      value: BufferTankOperatingModeType.Heating,
      viewValue: this.translate.instant(
        '+INSTALLATIONS.INSTALLATION.PANEL.BUFFER_TANK.MODE_HEATING'
      ),
    },
  ];
  selectedBufferTankMode: BufferTankOperatingModeType;
  bufferTankTemperatureMain$: Observable<Feature<StatusValueDefaultFeature, ActiveModeCommands>>;

  private deviceId: string;
  private ngUnsubscribe$: Subject<void> = new Subject();

  constructor(
    private devicesStore: DevicesStore,
    private installationFeaturesStore: InstallationFeaturesStore,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.devicesStore.selectedDevice$
      .pipe(
        filter((device) => !!device),
        take(1),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe((device) => {
        this.deviceId = device.id;
        this.runSubscriptionsAfterGettingDeviceId();
      });
  }

  private runSubscriptionsAfterGettingDeviceId(): void {
    this.handleBufferTankModeFeature();

    this.bufferTankTemperatureMain$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.bufferTankTemperatureMain,
      this.deviceId
    );
  }

  private handleBufferTankModeFeature(): void {
    this.installationFeaturesStore
      .selectInstallationDeviceFeature<IsStringValueProperty, never>(
        FeatureNames.bufferCylinderOperatingModes,
        this.deviceId
      )
      .pipe(distinctUntilChanged(isEqual), takeUntil(this.ngUnsubscribe$))
      .subscribe((feature) => {
        const mode: BufferTankOperatingModeType = feature?.properties?.value?.value;
        this.isOperatingModeEditable = feature?.commands?.setMode?.isExecutable;

        if (isReady(feature) && mode) {
          this.selectedBufferTankMode = mode;
        } else {
          this.availableBufferModes = [];
        }
      });
  }

  onBufferTankModeChange(mode: BufferTankOperatingModeType): void {
    if (mode === this.selectedBufferTankMode) {
      return;
    }

    this.installationFeaturesStore.updateFeature(
      FeatureNames.bufferCylinderOperatingModes,
      mode,
      this.featurePropertyKey.value,
      FeatureActionFields.mode,
      FeatureCommands.setMode
    );
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
